import {Box, Radio, Select, Text} from '@mantine/core';
import {useForm} from '@mantine/form';
import {useRouter} from 'next/router';
import {type Dispatch, type SetStateAction, useEffect, useState} from 'react';
import type {
  GraduationYearsHotReturnValueType,
  MasterGroupedJobTypesReturnValueType,
  RecruitmentTagsReturnValueType,
  RecruitmentTypesReturnValueType,
} from '../../../../../core/repositories/recruitingInfoRepository';
import type {IndustryCategoriesReturnValueType} from '../../../../../core/repositories/reportsRepository';
import {useHandlePromoPopup} from '../../../../../shared/services/useHandlePromoPopup';
import {useHandleCheckboxChange} from '../../../usecase/useHandleCheckboxChange';
import type {RecruitmentOrderType} from '../../../usecase/useHandleRecruitingInfoForPC';
import {CheckboxInputForFilter} from './FilterCheckbox';

type Props = {
  industryCategories?: IndustryCategoriesReturnValueType;
  recruitmentTags?: RecruitmentTagsReturnValueType;
  recruitmentTypes?: RecruitmentTypesReturnValueType;
  masterGroupedJobTypes?: MasterGroupedJobTypesReturnValueType;
  graduationYearsHots?: GraduationYearsHotReturnValueType;
  filterCategories: number[];
  filterRecruitmentTags: number[];
  filterRecruitmentTypes: number[];
  filterGroupedJobTypes: number[];
  filterGraduationYear?: number;
  setRecruitmentOrder: Dispatch<SetStateAction<RecruitmentOrderType>>;
  setFilterCategories: Dispatch<SetStateAction<number[]>>;
  setFilterRecruitmentTags: Dispatch<SetStateAction<number[]>>;
  setFilterRecruitmentTypes: Dispatch<SetStateAction<number[]>>;
  setFilterGroupedJobTypes: Dispatch<SetStateAction<number[]>>;
  setFilterGraduationYear: Dispatch<SetStateAction<number | undefined>>;
};

type ClickEvent = React.MouseEvent<HTMLDivElement>;

export const LeftColumn: React.FC<Props> = ({
  industryCategories,
  filterCategories,
  filterRecruitmentTags,
  filterRecruitmentTypes,
  filterGroupedJobTypes,
  filterGraduationYear,
  recruitmentTags,
  recruitmentTypes,
  masterGroupedJobTypes,
  graduationYearsHots,
  setRecruitmentOrder,
  setFilterCategories,
  setFilterRecruitmentTags,
  setFilterRecruitmentTypes,
  setFilterGroupedJobTypes,
  setFilterGraduationYear,
}) => {
  const router = useRouter();
  const {order} = router.query;
  const [localOrder, setLocalOrder] = useState<
    RecruitmentOrderType | undefined
  >();
  useEffect(() => {
    setLocalOrder(order as RecruitmentOrderType);
    form.setValues({
      recruitingInfo: {
        ...form.values.recruitingInfo,
        order: (order ?? 'recommend') as RecruitmentOrderType,
      },
    });
  }, [order]);

  const {user} = useHandlePromoPopup();

  // 卒年の初期値を設定
  useEffect(() => {
    if (filterGraduationYear === undefined) {
      setFilterGraduationYear(initializeGraduationYear);
    } else {
      setFilterGraduationYear(Number(filterGraduationYear));
    }
  }, [user?.graduationYear, graduationYearsHots?.ok]);

  const initializeGraduationYear = () => {
    if (
      user &&
      graduationYearsHots?.ok &&
      graduationYearsHots.value.years.includes(user.graduationYear)
    ) {
      return user.graduationYear;
    }

    return undefined;
  };

  const createGraduationYearState = (gy?: number): string => {
    if (gy !== undefined) return gy.toString();
    return graduationYearToString(initializeGraduationYear());
  };

  const graduationYearToString = (gy: number | undefined): string => {
    if (gy === undefined) {
      return '0';
    }

    return gy.toString();
  };

  type RecruitingInfoForm = {
    graduationYearsHot: string;
    order: RecruitmentOrderType;
    recruitmentTag: number;
    recruitmentType: number;
    groupedJobType: number;
  };

  const form = useForm<{
    recruitingInfo: RecruitingInfoForm;
  }>({
    initialValues: {
      recruitingInfo: {
        graduationYearsHot: graduationYearToString(filterGraduationYear),
        order: localOrder || 'recommend',
        recruitmentTag: 0,
        recruitmentType: 0,
        groupedJobType: 0,
      },
    },
  });

  const handelChangeRadio = (s: ClickEvent) => {
    const target = s.target as HTMLInputElement;
    if (target.value) {
      setRecruitmentOrder(target.value as RecruitmentOrderType);
      form.setValues({
        recruitingInfo: {
          ...form.values.recruitingInfo,
          order: target.value as RecruitmentOrderType,
        },
      });
    }
  };

  const handelChangeSelect = (newValue: string) => {
    if (newValue === 'undefined') {
      setFilterGraduationYear(undefined);
      return;
    }

    setFilterGraduationYear(Number(newValue));
  };

  const sortChoices = [
    {
      label: 'おすすめ順',
      value: 'recommend',
    },
    {
      label: '新着順',
      value: 'recent',
    },
    {
      label: '締切順',
      value: 'deadline',
    },
    {
      label: '開催順',
      value: 'eventDate',
    },
  ];

  return (
    <>
      <Box className="w-full rounded-[16px] shadow-card bg-white relative">
        <Box className="px-12 py-8 border-b-2 border-line text-base font-bold leading-snug">
          並べ替え
        </Box>
        <Radio.Group
          onClick={handelChangeRadio}
          className="px-8 py-4"
          {...form.getInputProps('recruitingInfo.order')}
        >
          {sortChoices.map((item) => (
            <Radio
              key={item.value}
              value={item.value}
              label={item.label}
              className="my-8 whitespace-nowrap"
            />
          ))}
        </Radio.Group>
      </Box>
      <Box className="w-full  flex flex-col rounded-[16px] shadow-card">
        <Text className="mb-2 rounded-t-[16px] bg-white text-black font-bold px-12 py-8 text-base">
          絞り込む
        </Text>
        <Box>
          <Text className="mb-2 bg-background-2 px-12 py-8 font-bold text-black text-base">
            対象で絞り込む
          </Text>
          <Box className="p-8 flex flex-col gap-y-4 items-start bg-white">
            {recruitmentTags?.ok &&
              recruitmentTags.value.tags.map((recruitmentTag) => (
                <CheckboxInputForFilter
                  key={`recruitmentTag-${recruitmentTag.id}`}
                  inputId={`recruitmentTag-${recruitmentTag.id}`}
                  checked={filterRecruitmentTags.includes(recruitmentTag.id)}
                  onChange={() => {
                    useHandleCheckboxChange(
                      recruitmentTag.id,
                      setFilterRecruitmentTags,
                    );
                  }}
                  labelText={recruitmentTag.longName}
                  hasForEngineer={user?.isEngineer && recruitmentTag.id === 3}
                />
              ))}
          </Box>
        </Box>
        <Box>
          <Text className="mb-2 bg-background-2 px-12 py-8 font-bold text-black text-base">
            業界で絞り込む
          </Text>
          <Box className="p-12 flex flex-col gap-y-4 items-start bg-white">
            {industryCategories?.ok &&
              industryCategories.value.industryCategories.map(
                (industryCategory) => (
                  <CheckboxInputForFilter
                    key={`industryCategory-${industryCategory.id}`}
                    inputId={`industryCategory-${industryCategory.id}`}
                    checked={filterCategories.includes(industryCategory.id)}
                    onChange={() => {
                      useHandleCheckboxChange(
                        industryCategory.id,
                        setFilterCategories,
                      );
                    }}
                    labelText={industryCategory.name}
                  />
                ),
              )}
          </Box>
        </Box>
        <Box>
          <Text className="mb-2 bg-background-2 px-12 py-8 font-bold text-black text-base">
            職種で絞り込む
          </Text>
          <Box className="p-12 flex flex-col gap-y-4 items-start bg-white">
            {masterGroupedJobTypes?.ok &&
              masterGroupedJobTypes.value.groupedJobTypes.map(
                (groupedJobType) => (
                  <CheckboxInputForFilter
                    key={`groupedJobType-${groupedJobType.id}`}
                    inputId={`groupedJobType-${groupedJobType.id}`}
                    checked={filterGroupedJobTypes.includes(groupedJobType.id)}
                    onChange={() => {
                      useHandleCheckboxChange(
                        groupedJobType.id,
                        setFilterGroupedJobTypes,
                      );
                    }}
                    labelText={groupedJobType.name}
                  />
                ),
              )}
          </Box>
        </Box>
        <Box>
          <Text className="mb-2 bg-background-2 px-12 py-8 font-bold text-black text-base">
            種類で絞り込む
          </Text>
          <Box className="p-12 flex flex-col gap-y-4 items-start bg-white">
            {recruitmentTypes?.ok &&
              recruitmentTypes.value.types.map((type) => (
                <CheckboxInputForFilter
                  key={`recruitmentType-${type.id}`}
                  inputId={`recruitmentType-${type.id}`}
                  checked={filterRecruitmentTypes.includes(type.id)}
                  onChange={() => {
                    useHandleCheckboxChange(type.id, setFilterRecruitmentTypes);
                  }}
                  labelText={type.name}
                />
              ))}
          </Box>
        </Box>
        <Box>
          <Text className="mb-2 px-12 py-8 font-bold text-black text-base">
            対象卒年で絞り込む
          </Text>
          <Box className="bg-white rounded-b-[16px]">
            {graduationYearsHots?.ok && (
              <Select
                className="p-12"
                maxDropdownHeight={500}
                data={convertGraduationYears(graduationYearsHots.value.years)}
                value={createGraduationYearState(filterGraduationYear)}
                onChange={(newValue) => {
                  handelChangeSelect(newValue!);
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

type ConvertedGraduationYearsHot = {
  value: string;
  label: string;
};

export function convertGraduationYears(
  inputData: number[],
): ConvertedGraduationYearsHot[] {
  const defaultData = [
    {
      value: '0',
      label: 'すべて',
    },
    {
      value: '-1',
      label: '卒年不問',
    },
  ];

  return [
    ...defaultData,
    ...inputData.map((year) => ({
      value: year.toString(),
      label: `${year.toString()}卒`,
    })),
  ];
}
